import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(res => setPost(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#2D5016] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (!post) {
    return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><h1 className="font-heading text-3xl">Article introuvable</h1><Link to="/blog" className="text-[#2D5016] underline font-body">Retour au blog</Link></div>;
  }

  return (
    <div>
      <div className="bg-[#EFEBE5] px-6 md:px-12 lg:px-24 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm font-body text-[#6B6B6B]">
          <Link to="/" className="hover:text-[#2D5016]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-[#2D5016]">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#2D5016] font-medium truncate max-w-[200px]">{post.title}</span>
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-24">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-3 text-sm font-body text-white/70">
              <span>{post.published_date}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {post.read_time} min de lecture</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-testid="blog-article-title">{post.title}</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <article className="py-16 md:py-24 px-6 md:px-12 lg:px-24" data-testid="blog-article-content">
        <div
          className="max-w-3xl mx-auto prose prose-lg font-body text-[#2C2C2C]/85 leading-relaxed
          [&_h2]:font-heading [&_h2]:text-2xl [&_h2]:md:text-3xl [&_h2]:font-semibold [&_h2]:text-[#2C2C2C] [&_h2]:mt-10 [&_h2]:mb-4
          [&_h3]:font-heading [&_h3]:text-xl [&_h3]:font-medium [&_h3]:text-[#2D5016] [&_h3]:mt-8 [&_h3]:mb-3
          [&_p]:mb-4 [&_p]:text-base
          [&_a]:text-[#2D5016] [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </article>

      {/* CTA to destination */}
      {post.destination_slug && (
        <section className="py-12 px-6 md:px-12 lg:px-24 bg-[#EFEBE5]">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="font-heading text-2xl font-semibold text-[#2C2C2C] mb-4">Envie de decouvrir cette destination ?</h3>
            <Link
              to={`/destinations/${post.destination_slug}`}
              className="inline-flex items-center gap-2 bg-[#2D5016] text-[#FDF8F0] rounded-full px-8 py-3 font-body font-medium hover:bg-[#2D5016]/90 transition-all"
              data-testid="blog-cta-destination"
            >
              Voir le circuit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
